import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import VendorLayout from "../../components/VendorLayout";
import { api, formatEUR, formatDateFR } from "../../lib/api";
import { emitDataChanged } from "../../lib/dataBus";
import { toast } from "sonner";
import { confirmAction } from "../../components/ConfirmDialog";
import {
  Loader2, Search, Plus, X, User, Users, Mail, Phone, CalendarCheck,
  Hash, Pencil, Trash2, ArrowRight, ArrowLeft, AlertTriangle, FileText, Save,
  Archive, RotateCcw, MapPin, Building2, Star,
} from "lucide-react";
import SegmentedPills from "../../components/ui/SegmentedPills";
import TabToolbar from "../../components/pilotage/TabToolbar";
import ViewToggle, { useViewMode } from "../../components/pilotage/ViewToggle";
import EmptyState from "../../components/pilotage/EmptyState";
import ClientDocuments from "../../components/pilotage/ClientDocuments";
import { CsvMenu } from "../../components/pilotage/CsvActions";
import { Skeleton } from "../../components/ui/Skeleton";
import { ScanLicenseButton } from "../../components/pilotage/ScanLicenseButton";
import ClientCreateForm from "../../components/pilotage/ClientCreateForm";

export { ScanLicenseButton };

export default function VendorClients({ embedded = false }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | client | prospect | archived
  const [selected, setSelected] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Filtre de recherche transmis par le Copilote via l'URL (?q=)
  useEffect(() => {
    const q = searchParams.get("q");
    if (q && q.trim()) setQuery(q.trim().slice(0, 60));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Lien profond ?client=email (cartes du Copilot) → ouvre la fiche
  useEffect(() => {
    const email = searchParams.get("client");
    if (!email || !clients.length) return;
    const c = clients.find((x) => x.email === email);
    if (c) {
      setSelected(c);
      setShowAdd(false);
      const next = new URLSearchParams(searchParams);
      next.delete("client");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients]);

  const load = () => {
    api.get(`/vendor/clients${statusFilter === "archived" ? "?archived=1" : ""}`)
      .then((c) => setClients(c.data.clients || []))
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [statusFilter]);

  const filtered = useMemo(() => {
    let list = clients;
    if (statusFilter === "client" || statusFilter === "prospect") list = list.filter((c) => (c.status || "client") === statusFilter);
    if (query) {
      const q = query.toLowerCase();
      list = list.filter((c) => c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q) || c.phone?.includes(q));
    }
    return list;
  }, [clients, query, statusFilter]);

  const counts = useMemo(() => ({
    all: clients.length,
    client: clients.filter((c) => (c.status || "client") === "client").length,
    prospect: clients.filter((c) => c.status === "prospect").length,
  }), [clients]);

  const [viewMode, setViewMode] = useViewMode("clients", "list");
  const selectClient = (c) => { setSelected(c); setShowAdd(false); };
  const closeSidebar = () => setSelected(null);

  const content = (
    <div className="flex h-full overflow-hidden">
        {/* LIST */}
        <div className={`flex-1 min-w-0 flex flex-col ${selected || showAdd ? "hidden" : "flex"}`}>
          <div className="p-4 sm:p-6 pb-0">
            {/* Hidden trigger (activé par le + global) */}
            <button onClick={() => { setShowAdd("client"); setSelected(null); }} className="hidden" data-testid="add-client-btn" aria-hidden="true" tabIndex={-1}>hidden</button>
            <button onClick={() => { setShowAdd("prospect"); setSelected(null); }} className="hidden" data-testid="add-prospect-btn" aria-hidden="true" tabIndex={-1}>hidden</button>
            <TabToolbar
              title="Clients"
              count={counts.all}
              search={query}
              onSearch={setQuery}
              searchPlaceholder="Rechercher un client..."
              searchTestid="search-clients"
              filtersActive={statusFilter !== "all" ? 1 : 0}
              filters={
                <SegmentedPills
                  items={[
                    { key: "all", label: "Tous", count: counts.all },
                    { key: "client", label: "Clients", count: counts.client },
                    { key: "prospect", label: "Prospects", count: counts.prospect },
                    { key: "archived", label: "Archivés" },
                  ]}
                  value={statusFilter}
                  onChange={setStatusFilter}
                  testid="clients-status-filter"
                  variant="pills"
                />
              }
              viewToggle={<ViewToggle value={viewMode} onChange={setViewMode} testid="clients-view-toggle" />}
              addLabel="Client"
              onAdd={() => { setShowAdd(true); setSelected(null); }}
              addTestid="add-client-toolbar-btn"
              permissionModule="clients"
              extra={
                <CsvMenu exportPath="/vendor/clients/export" exportFilename="clients.csv" importPath="/vendor/clients/import" onDone={load} testid="clients-csv-menu" />
              }
            />
          </div>

          <div className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-8 pt-4 pb-8">
            {loading ? (
              <div className="space-y-2" data-testid="clients-skeleton">
                {Array.from({ length: 7 }).map((_, i) => (
                  <div key={i} className="w-full p-4 rounded-xl border border-slate-200 bg-white flex items-center gap-3">
                    <Skeleton className="h-9 w-9 rounded-full shrink-0" />
                    <div className="flex-1">
                      <Skeleton className="h-3.5 w-40 mb-2" />
                      <Skeleton className="h-3 w-28" />
                    </div>
                    <Skeleton className="h-3 w-16" />
                  </div>
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <EmptyState
                icon={Users}
                title="Aucun client trouvé"
                description="Ajoutez un client ou ajustez votre recherche."
                testid="clients-empty"
              />
            ) : (
              <div className={viewMode === "cards" ? "grid sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3" : "space-y-2"} data-testid="clients-list">
                {filtered.map((c) => (
                  <button key={c.email} onClick={() => selectClient(c)} className={`w-full text-left p-4 rounded-xl border transition-all ${selected?.email === c.email ? "border-violet-300 bg-violet-50/50" : "border-slate-200 bg-white hover:border-slate-300"}`} data-testid={`client-${c.email}`}>
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center font-heading font-semibold text-sm shrink-0">
                        {c.name?.[0]?.toUpperCase() || "?"}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate flex items-center gap-1.5">
                          {c.name}
                          {c.status === "prospect" && <span className="px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider bg-amber-100 text-amber-700 rounded">Prospect</span>}
                        </div>
                        <div className="text-xs text-slate-500 truncate">{c.email}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-sm font-semibold text-slate-900">{c.bookings_count} rés.</div>
                        <div className="text-xs text-slate-500">{formatEUR(c.total_spent)}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* FICHE CLIENT — pleine page */}
        {selected && (
          <ClientSidebar
            client={selected}
            onClose={closeSidebar}
            onUpdate={(updated) => { setSelected(updated); load(); emitDataChanged("clients"); }}
            onDelete={() => { closeSidebar(); load(); emitDataChanged("clients"); }}
          />
        )}

        {/* NOUVELLE FICHE CLIENT — pleine page */}
        {showAdd && !selected && (
          <AddClientSidebar defaultStatus={showAdd === "prospect" ? "prospect" : "client"} onClose={() => setShowAdd(false)} onCreated={() => { setShowAdd(false); load(); emitDataChanged("clients"); }} />
        )}
      </div>
  );

  return embedded ? content : <VendorLayout>{content}</VendorLayout>;
}

// ─── Client Detail Sidebar ───
const CLIENT_FORM_FIELDS = ["name", "phone", "notes", "birth_date", "license_number", "license_expiry", "civility", "company", "phone2", "address", "zip_code", "city", "country", "language", "license_issued_date", "license_country"];
const clientToForm = (client) => Object.fromEntries(CLIENT_FORM_FIELDS.map((f) => [f, client[f] || ""]));

function ClientSidebar({ client, onClose, onUpdate, onDelete }) {
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(clientToForm(client));
  const [bookings, setBookings] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setForm(clientToForm(client));
    setEditing(false);
    api.get(`/vendor/clients/${encodeURIComponent(client.email)}/bookings`)
      .then(({ data }) => setBookings(data.bookings || []))
      .catch(() => setBookings([]));
    api.get("/vendor/quotes")
      .then(({ data }) => setQuotes((data.quotes || []).filter((q) => q.client_email === client.email)))
      .catch(() => setQuotes([]));
    api.get(`/vendor/clients/${encodeURIComponent(client.email)}/reviews`)
      .then(({ data }) => setReviews(data.reviews || []))
      .catch(() => setReviews([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.email]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/vendor/clients/${encodeURIComponent(client.email)}`, form);
      onUpdate({ ...client, ...form });
      setEditing(false);
      toast.success("Client mis à jour");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible d'enregistrer les modifications");
    }
    setSaving(false);
  };

  const handleArchive = async () => {
    const verb = client.archived ? "Restaurer" : "Archiver";
    if (!(await confirmAction({
      title: `${verb} « ${client.name} » ?`,
      message: client.archived
        ? "La fiche client sera restaurée dans votre liste active."
        : "La fiche client sera archivée : elle n'apparaîtra plus dans la liste active, mais l'historique est conservé.",
      confirmLabel: `Confirmer : ${verb.toLowerCase()}`,
      variant: "warning",
    }))) return;
    try {
      const { data } = await api.post(`/vendor/clients/${encodeURIComponent(client.email)}/archive`);
      toast.success(data.archived ? "Client archivé" : "Client restauré");
      onDelete();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleDelete = async () => {
    if (!(await confirmAction({
      title: "Supprimer définitivement ce client ?",
      message: `La fiche de ${client.name} (${client.email}) et ses documents seront définitivement supprimés. Cette action est IRRÉVERSIBLE.`,
      confirmLabel: "Supprimer le client",
      variant: "danger",
      requireText: "SUPPRIMER",
    }))) return;
    setDeleting(true);
    try {
      await api.delete(`/vendor/clients/${encodeURIComponent(client.email)}`);
      toast.success("Client supprimé");
      onDelete();
    } catch (err) {
      if (err.response?.status === 409) {
        if (await confirmAction({
          title: "Suppression impossible — archiver à la place ?",
          message: "Ce client a un historique de réservations et ne peut pas être supprimé. L'archiver le retirera de la liste active sans perdre l'historique.",
          confirmLabel: "Archiver le client",
          variant: "warning",
        })) {
          await api.post(`/vendor/clients/${encodeURIComponent(client.email)}/archive`);
          toast.success("Client archivé");
          onDelete();
        }
      } else {
        toast.error(err.response?.data?.detail || "Erreur");
      }
    }
    setDeleting(false);
  };

  return (
    <div className="w-full flex flex-col overflow-y-auto" data-testid="client-sidebar">
      {/* Bandeau standard */}
      <div className="px-4 sm:px-6 lg:px-8 pt-5">
        <button onClick={onClose} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-violet-700 transition-colors mb-3" data-testid="close-sidebar">
          <ArrowLeft className="h-3.5 w-3.5" /> Retour aux clients
        </button>
        <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-violet-500">Fiche client</div>
        <h1 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight text-slate-900 mt-1 break-words flex items-center gap-2 flex-wrap">
          {client.civility && <span className="text-slate-400 text-xl">{client.civility}</span>}
          {client.name}
          {client.archived && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-800 text-white text-xs font-semibold" data-testid="client-archived-badge">
              <Archive className="h-3 w-3" /> Archivé
            </span>
          )}
        </h1>
        {client.company && (
          <div className="text-sm text-slate-500 mt-1 flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" /> {client.company}</div>
        )}
      </div>

      <div className="p-4 sm:p-6 lg:p-8 grid sm:grid-cols-2 xl:grid-cols-3 gap-5 items-start flex-1">
        {/* Info */}
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center font-heading font-semibold text-lg">
            {client.name?.[0]?.toUpperCase() || "?"}
          </div>
          <div>
            {editing ? (
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="text-sm font-medium text-slate-900 border border-slate-200 rounded-lg px-2 py-1 w-full outline-none focus:ring-1 focus:ring-violet-600" data-testid="edit-name" />
            ) : (
              <div className="text-sm font-medium text-slate-900">{client.name}</div>
            )}
            <div className="text-xs text-slate-500">{client.email}</div>
          </div>
        </div>

        {/* Contact & coordonnées */}
        <div className="space-y-2" data-testid="client-contact-card">
          <div className="flex items-center gap-2 text-sm">
            <Mail className="h-3.5 w-3.5 text-slate-400" />
            <span className="text-slate-600">{client.email}</span>
          </div>
          {editing ? (
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <SmallField label="Civilité" value={form.civility} onChange={(v) => setForm({ ...form, civility: v })} placeholder="M. / Mme" testid="edit-civility" />
                <SmallField label="Société" value={form.company} onChange={(v) => setForm({ ...form, company: v })} testid="edit-company" />
                <SmallField label="Tél. mobile" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="edit-phone" />
                <SmallField label="Tél. fixe" value={form.phone2} onChange={(v) => setForm({ ...form, phone2: v })} testid="edit-phone2" />
              </div>
              <SmallField label="Adresse" value={form.address} onChange={(v) => setForm({ ...form, address: v })} testid="edit-address" />
              <div className="grid grid-cols-3 gap-2">
                <SmallField label="CP" value={form.zip_code} onChange={(v) => setForm({ ...form, zip_code: v })} testid="edit-zip" />
                <SmallField label="Ville" value={form.city} onChange={(v) => setForm({ ...form, city: v })} testid="edit-city" />
                <SmallField label="Pays" value={form.country} onChange={(v) => setForm({ ...form, country: v })} testid="edit-country" />
              </div>
              <SmallField label="Langue" value={form.language} onChange={(v) => setForm({ ...form, language: v })} placeholder="Français" testid="edit-language" />
            </div>
          ) : (
            <>
              {client.phone && (
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="h-3.5 w-3.5 text-slate-400" />
                  <span className="text-slate-600">{client.phone}</span>
                </div>
              )}
              {client.phone2 && (
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="h-3.5 w-3.5 text-slate-300" />
                  <span className="text-slate-600">{client.phone2} <span className="text-[10px] text-slate-400">(fixe)</span></span>
                </div>
              )}
              {(client.address || client.city) && (
                <div className="flex items-start gap-2 text-sm">
                  <MapPin className="h-3.5 w-3.5 text-slate-400 mt-0.5" />
                  <span className="text-slate-600">
                    {client.address}{client.address && (client.zip_code || client.city) ? ", " : ""}{[client.zip_code, client.city].filter(Boolean).join(" ")}{client.country ? ` · ${client.country}` : ""}
                  </span>
                </div>
              )}
              {client.language && <div className="text-xs text-slate-400">Langue : {client.language}</div>}
            </>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-xl bg-slate-50 p-3">
            <div className="text-[10px] uppercase font-bold text-slate-400">Réservations</div>
            <div className="font-heading text-xl font-medium text-slate-900 mt-0.5">{client.bookings_count}</div>
          </div>
          <div className="rounded-xl bg-slate-50 p-3">
            <div className="text-[10px] uppercase font-bold text-slate-400">Total dépensé</div>
            <div className="font-heading text-xl font-medium text-slate-900 mt-0.5">{formatEUR(client.total_spent)}</div>
          </div>
        </div>

        {/* Notes */}
        <div>
          <div className="text-[10px] uppercase font-bold text-slate-400 mb-1">Notes</div>
          {editing ? (
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2 outline-none focus:ring-1 focus:ring-violet-600 resize-none" placeholder="Notes internes..." data-testid="edit-notes" />
          ) : (
            <p className="text-sm text-slate-600">{client.notes || "Aucune note."}</p>
          )}
        </div>

        {/* Permis de conduire */}
        <div>
          <div className="text-[10px] uppercase font-bold text-slate-400 mb-2">Permis de conduire</div>
          {editing ? (
            <div className="space-y-2">
              <SmallField label="N° de permis" value={form.license_number} onChange={(v) => setForm({ ...form, license_number: v })} testid="edit-license-number" />
              <SmallField label="Date de naissance" value={form.birth_date} onChange={(v) => setForm({ ...form, birth_date: v })} placeholder="JJ/MM/AAAA" testid="edit-birth-date" />
              <SmallField label="Validité du permis" value={form.license_expiry} onChange={(v) => setForm({ ...form, license_expiry: v })} placeholder="JJ/MM/AAAA" testid="edit-license-expiry" />
              <SmallField label="Date d'obtention" value={form.license_issued_date} onChange={(v) => setForm({ ...form, license_issued_date: v })} placeholder="JJ/MM/AAAA" testid="edit-license-issued" />
              <SmallField label="Pays de délivrance" value={form.license_country} onChange={(v) => setForm({ ...form, license_country: v })} placeholder="France" testid="edit-license-country" />
            </div>
          ) : (
            <div className="rounded-xl bg-slate-50 p-3 space-y-1.5 text-xs" data-testid="client-license-card">
              <div className="flex justify-between gap-2"><span className="text-slate-400">N° de permis</span><span className="font-medium text-slate-700">{client.license_number || "—"}</span></div>
              <div className="flex justify-between gap-2"><span className="text-slate-400">Date de naissance</span><span className="font-medium text-slate-700">{client.birth_date || "—"}</span></div>
              <div className="flex justify-between gap-2"><span className="text-slate-400">Validité</span><span className="font-medium text-slate-700">{client.license_expiry || "—"}</span></div>
              <div className="flex justify-between gap-2"><span className="text-slate-400">Obtenu le</span><span className="font-medium text-slate-700">{client.license_issued_date || "—"}</span></div>
              <div className="flex justify-between gap-2"><span className="text-slate-400">Pays</span><span className="font-medium text-slate-700">{client.license_country || "—"}</span></div>
            </div>
          )}
          <div className="mt-2">
            <ScanLicenseButton
              compact
              onExtract={async (d) => {
                const patch = {
                  birth_date: d.birth_date || client.birth_date || "",
                  license_number: d.license_number || client.license_number || "",
                  license_expiry: d.expiry_date || client.license_expiry || "",
                };
                try {
                  await api.put(`/vendor/clients/${encodeURIComponent(client.email)}`, patch);
                  onUpdate({ ...client, ...patch });
                  toast.success("Permis lu — fiche client mise à jour");
                } catch { toast.error("Impossible d'enregistrer les données du permis"); }
              }}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          {editing ? (
            <>
              <button onClick={handleSave} disabled={saving} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-50" data-testid="save-client">
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Enregistrer
              </button>
              <button onClick={() => setEditing(false)} className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">Annuler</button>
            </>
          ) : (
            <button onClick={() => setEditing(true)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50" data-testid="edit-client">
              <Pencil className="h-3.5 w-3.5" /> Modifier
            </button>
          )}
          <button
            onClick={() => navigate(`/vendor/pilotage?tab=bookings&booking=new&client_name=${encodeURIComponent(client.name)}&client_email=${encodeURIComponent(client.email)}&client_phone=${encodeURIComponent(client.phone || "")}`)}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full btn-primary text-sm font-semibold"
            data-testid="create-booking-from-client"
          >
            <CalendarCheck className="h-3.5 w-3.5" /> Réservation
          </button>
          <button onClick={handleArchive} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50" data-testid="archive-client-btn">
            {client.archived ? <RotateCcw className="h-3.5 w-3.5" /> : <Archive className="h-3.5 w-3.5" />} {client.archived ? "Restaurer" : "Archiver"}
          </button>
        </div>

        {/* Booking history */}
        <div>
          <div className="text-[10px] uppercase font-bold text-slate-400 mb-2">Historique réservations</div>
          {bookings.length === 0 ? (
            <p className="text-xs text-slate-400">Aucune réservation.</p>
          ) : (
            <div className="space-y-2" data-testid="client-bookings">
              {bookings.map((b) => (
                <Link key={b.id} to={`/vendor/pilotage?tab=bookings&booking=${b.id}`} className="flex items-center gap-3 p-3 rounded-xl border border-slate-100 bg-slate-50/50 hover:border-slate-300 transition-colors" data-testid={`client-booking-${b.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-slate-900 truncate">{b.vehicle_name}</div>
                    <div className="text-[10px] text-slate-500">{formatDateFR(b.start_date)} → {formatDateFR(b.end_date)}</div>
                  </div>
                  <div className="text-xs font-semibold text-slate-900">{formatEUR(b.total)}</div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Devis */}
        <div>
          <div className="text-[10px] uppercase font-bold text-slate-400 mb-2">Devis</div>
          {quotes.length === 0 ? (
            <p className="text-xs text-slate-400">Aucun devis.</p>
          ) : (
            <div className="space-y-2" data-testid="client-quotes">
              {quotes.map((q) => (
                <Link key={q.id} to={`/vendor/pilotage?tab=quotes${q.status === "accepted" && q.booking_id ? `&booking=${q.booking_id}` : `&quote=${q.id}`}`} className="flex items-center gap-3 p-3 rounded-xl border border-slate-100 bg-slate-50/50 hover:border-slate-300 transition-colors" data-testid={`client-quote-${q.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-slate-900 truncate">{q.vehicle_name || "Devis"}</div>
                    <div className="text-[10px] text-slate-500">{formatDateFR(q.start_date)} → {formatDateFR(q.end_date)}</div>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${q.status === "accepted" ? "bg-emerald-100 text-emerald-700" : q.status === "sent" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-600"}`}>
                    {q.status === "accepted" ? "Accepté" : q.status === "sent" ? "Envoyé" : q.status === "declined" ? "Refusé" : "Brouillon"}
                  </span>
                  <div className="text-xs font-semibold text-slate-900">{formatEUR(q.total)}</div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Avis */}
        <div>
          <div className="text-[10px] uppercase font-bold text-slate-400 mb-2">Avis boutique</div>
          {reviews.length === 0 ? (
            <p className="text-xs text-slate-400">Aucun avis laissé par ce client.</p>
          ) : (
            <div className="space-y-2" data-testid="client-reviews">
              {reviews.map((r) => (
                <div key={r.id} className="p-3 rounded-xl border border-slate-100 bg-slate-50/50" data-testid={`client-review-${r.id}`}>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <Star key={i} className={`h-3 w-3 ${i <= r.rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
                      ))}
                    </div>
                    <span className="text-[10px] text-slate-400">{formatDateFR((r.created_at || "").slice(0, 10))}</span>
                    {!r.published && <span className="px-1.5 py-0.5 rounded bg-slate-200 text-[9px] font-bold text-slate-600">Masqué</span>}
                  </div>
                  {r.comment && <p className="mt-1.5 text-xs text-slate-600 leading-relaxed">{r.comment}</p>}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Documents */}
        <div>
          <div className="text-[10px] uppercase font-bold text-slate-400 mb-2">Documents</div>
          <ClientDocuments clientEmail={client.email} />
        </div>

        {/* Delete */}
        {client.can_delete ? (
          <button onClick={handleDelete} disabled={deleting} className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl border border-red-200 text-red-600 text-sm font-medium hover:bg-red-50 transition-colors disabled:opacity-50" data-testid="delete-client">
            <Trash2 className="h-3.5 w-3.5" /> Supprimer ce client
          </button>
        ) : (
          <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-50 border border-amber-100 text-xs text-amber-700">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            Ce client ne peut pas être supprimé car il est lié à une réservation active.
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Add Client Sidebar ───
function AddClientSidebar({ onClose, onCreated, defaultStatus = "client" }) {
  return (
    <div className="w-full flex flex-col overflow-y-auto" data-testid="add-client-sidebar">
      <div className="px-4 sm:px-6 lg:px-8 pt-5">
        <button onClick={onClose} type="button" className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-violet-700 transition-colors mb-3" data-testid="add-client-back">
          <ArrowLeft className="h-3.5 w-3.5" /> Retour aux clients
        </button>
        <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-violet-500">Nouvelle fiche client</div>
        <h1 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight text-slate-900 mt-1">{defaultStatus === "prospect" ? "Nouveau prospect" : "Nouveau client"}</h1>
      </div>
      <div className="p-4 sm:p-6 lg:p-8 max-w-2xl w-full">
        <ClientCreateForm defaultStatus={defaultStatus} onCreated={() => onCreated()} onCancel={onClose} />
      </div>
    </div>
  );
}

// ─── Scan du permis de conduire (IA) ───


function SmallField({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="text-[10px] font-bold text-slate-500">{label}</label>
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="mt-0.5 w-full text-sm border border-slate-200 rounded-lg px-2.5 py-1.5 outline-none focus:ring-1 focus:ring-violet-600" data-testid={testid} />
    </div>
  );
}
